import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Button,
    IconButton,
    ListItemButton,
    ListItemText,
    Menu,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import CommonInput from '../../component/form/common-input.component';
import { FormFieldCell } from '../../component/form/form-field-cell.component';
import DialogComponent from '../../component/dialog/dialog.component';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import i18n from '../../i18n';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import MainPageState from '../main/main-page-states';
import { MenuLibraryManagementState, MENU_LIBRARY_WEEK_HOLIDAY_NUM } from './menu-library-management-state';
import { getMainGroupNameForDisplay } from '../../utils/menu-main-group-name-i18n';
import { MenuLanguageMenuButton } from './menu-library-language-menu-button.component';
import { AntSwitch } from '../../component/switch/IOSSwitch';
/** Figma 3114:45266 成人档头像图标（与详情绑定费用药丸一致） */
import { ReactComponent as MenuLibraryPriceAdultFaceIcon } from '../../static/images/svg/menu_library_price_adult_face.svg';
import { ReactComponent as ChildCareIcon } from '../../static/images/svg/child_care.svg';
import { ReactComponent as FaceSmileIcon } from '../../static/images/svg/mdi_face-outline.svg';
import { ReactComponent as AddIcon } from '../../static/images/svg/mingcute_add-fill.svg';
import { ReactComponent as SaveIcon } from '../../static/images/svg/ic_round-save.svg';
import { ReactComponent as ArrowDropdownIcon } from '../../static/images/svg/iconamoon_arrow-up-2-fill.svg';
/** 子分类详情 / 返回 */
/** Figma 1553:84529 — solar:copy-bold（一键复制星期价格） */
import { ReactComponent as CopyWeekPriceIcon } from '../../static/images/svg/solar_copy-bold.svg';
import {
    CreateMenuSubcategoryDraftRow,
    type SubcategoryDraftFieldKey,
} from './menu-library-create-subcategory-draft-row.component';
import { CreateMenuSubcategoryDetailPanel } from './menu-library-create-subcategory-detail-panel.component';
import { MenuLibraryAddDishDialogContent } from './menu-library-add-dish-dialog-content.component';
import { RestaurantStaticImageUpload } from '../../component/restaurant-static-image-upload.component';
import { buildRestaurantStaticImageDisplayUrl } from '../../utils/restaurant-static-image-url';
import './scss/menu-library-create-menu-dialog.style.scss';
import './scss/menu-library-menu-detail.style.scss';

/** 弹窗纸面相对视口的外边距（px），与主内容区留白一致 */
const CREATE_MENU_DIALOG_OUTER_MARGIN_PX = 16;

type FormProps = {
    /** 菜单库状态（新建弹窗字段与草稿） */
    state: MenuLibraryManagementState;
};

type ShellProps = {
    /** 菜单库状态（含 isCreateMenuDialogOpen、提交等） */
    state: MenuLibraryManagementState;
};

/** 星期值 → business_hour i18n key（与菜单详情一致；7 为节假日） */
function weekValueToLabelKey(week: number): string {
    if (week === MENU_LIBRARY_WEEK_HOLIDAY_NUM) {
        return 'menu_library_create_dialog_week_holiday';
    }
    const map: Record<number, string> = {
        0: 'business_hour_page_Sunday',
        1: 'business_hour_page_Monday',
        2: 'business_hour_page_Tuesday',
        3: 'business_hour_page_Wednesday',
        4: 'business_hour_page_Thursday',
        5: 'business_hour_page_Friday',
        6: 'business_hour_page_Saturday',
    };
    return map[week] ?? 'business_hour_page_Monday';
}

/** 绑定费用药丸编辑：与详情页 `PRICE_KEY_TO_EXTRA` 一致 */
type PriceEditFieldKey = 'adult' | 'child' | 'other';

const PRICE_KEY_TO_EXTRA: Record<PriceEditFieldKey, 'extra_normal' | 'extra_child' | 'extra_other'> = {
    adult: 'extra_normal',
    child: 'extra_child',
    other: 'extra_other',
};

/** 绑定费用星期药丸顺序：周一至周日 + 节假日（与 Figma 一致） */
const CREATE_MENU_WEEK_KEYS: Array<number | 'holiday'> = [1, 2, 3, 4, 5, 6, 0, 'holiday'];

function weekPillLabel(key: number | 'holiday'): string {
    if (key === 'holiday') {
        return i18n.t('menu_library_create_dialog_week_holiday');
    }
    return i18n.t(weekValueToLabelKey(key));
}

/**
 * 「新建菜单」弹窗表单区：Figma 1553:84493 — 白色信息卡、子类别行、底部新增子类别
 *
 * 子类别区布局约定：
 * - 中间为列表滚动区，仅此处纵向滚动；顶部信息卡与弹窗底栏不参与该滚动。
 * - 「新增菜单子类别」按钮始终在子类别区块的底边（占满弹窗中间剩余高度后贴底），不随列表滚动。
 * - 子类别行自上而下排列在按钮上方；每新增一行插在列表末尾，视觉上向下延伸，超出部分在列表区内滚动。
 */
const MenuLibraryCreateMenuDialogForm: React.FC<FormProps> = observer(({ state }) => {
    const drafts = state.createMenuSubcategoryDrafts;
    const selectedWeek = state.createMenuSelectedWeekKey;
    /** 子类别列表滚动容器：新增行后滚到底部，便于看到最新一行 */
    const subcategoryListScrollRef = useRef<HTMLDivElement>(null);
    const prevDraftCountRef = useRef(drafts.length);
    /** 绑定费用：药丸锚点与当前档位（与详情顶栏编辑一致） */
    const [priceEditMenuAnchor, setPriceEditMenuAnchor] = useState<HTMLElement | null>(null);
    const [priceEditMenuField, setPriceEditMenuField] = useState<PriceEditFieldKey | null>(null);
    /** 一键复制当前星期绑定费用至全部星期：确认弹窗（与 `FirstMenuDetail` 一致） */
    const [createCopyConfirmOpen, setCreateCopyConfirmOpen] = useState(false);

    /** 子类别行：当前处于输入态的列（点击文案切换为输入框，失焦后持久化占位） */
    const [subcategoryEdit, setSubcategoryEdit] = useState<{ tempId: string; field: SubcategoryDraftFieldKey } | null>(
        null,
    );
    /** 删除子类别：待确认的草稿行 tempId（与 AlertDialog open 绑定） */
    const [subcategoryDeleteConfirmTempId, setSubcategoryDeleteConfirmTempId] = useState<string | null>(null);

    /** 新建弹窗菜单图：暂存 File 的本地预览 URL（与 store 中 `createDialogPendingMenuImageFile` 同步） */
    const pendingCreateMenuImageFile = state.createDialogPendingMenuImageFile;
    const [createMenuImageObjectUrl, setCreateMenuImageObjectUrl] = useState<string | null>(null);
    useEffect(() => {
        if (!pendingCreateMenuImageFile) {
            setCreateMenuImageObjectUrl(null);
            return;
        }
        const url = URL.createObjectURL(pendingCreateMenuImageFile);
        setCreateMenuImageObjectUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [pendingCreateMenuImageFile]);

    const createMenuImageDisplayUrl = useMemo(
        () =>
            createMenuImageObjectUrl ??
            buildRestaurantStaticImageDisplayUrl(state.createDialogMenuImageRef || undefined),
        [createMenuImageObjectUrl, state.createDialogMenuImageRef],
    );

    /** 下拉项：占位 + 额外费用列表（observer 随 extraMenuData 更新） */
    const priceEditMenuItems = [
        { value: '', label: i18n.t('menu_library_detail_price_select_placeholder') },
        ...state.getExtraMenuDataOptions(),
    ];

    /** 药丸行：标签 + 货币 + 汇总价或 — */
    const formatPriceEditPillLine = (labelKey: string, sumDisplay: string) => {
        const amount = sumDisplay === '—' || sumDisplay.trim() === '' ? '—' : `${state.getModuleCurrencyName()} ${sumDisplay}`;
        return `${i18n.t(labelKey)} ${amount}`;
    };

    useEffect(() => {
        const el = subcategoryListScrollRef.current;
        const prev = prevDraftCountRef.current;
        if (drafts.length > prev && el) {
            el.scrollTop = el.scrollHeight;
        }
        prevDraftCountRef.current = drafts.length;
    }, [drafts.length]);

    if (state.createMenuSubcategoryDetailTempId) {
        // 子类别详情
        return (
            <CreateMenuSubcategoryDetailPanel
                state={state}
                subcategoryEdit={subcategoryEdit}
                setSubcategoryEdit={setSubcategoryEdit}
            />
        );
    }

    return (
        <Box className="menu-library-create-dialog menu-library-menu-detail">
            <Paper variant="outlined" className="menu-library-create-dialog__base-card" elevation={0}>
                <Box className="menu-library-create-dialog__base-card-inner">
                    <Box className="menu-library-create-dialog__base-left">
                        <Box className="menu-library-create-dialog__thumb-wrap">
                            <Box
                                className="menu-library-create-dialog__thumb"
                                sx={{ position: 'relative', minHeight: 165 }}
                            >
                                <RestaurantStaticImageUpload
                                    stringId={state.resolveRbpStringIdForApi()}
                                    kind="menu-image"
                                    uploadEntityId={state.createDialogPersistedMainGroupId}
                                    imageRef={state.createDialogMenuImageRef}
                                    displayUrl={createMenuImageDisplayUrl}
                                    deferStaticUpload
                                    onStagedStaticFile={(file) => state.setCreateDialogPendingMenuImageFile(file)}
                                    onDeferredStaticRemove={() => {
                                        if (state.createDialogPendingMenuImageFile) {
                                            state.setCreateDialogPendingMenuImageFile(null);
                                            return;
                                        }
                                        state.markCreateDialogPendingRemoveServerMenuImage();
                                    }}
                                    disabled={state.isSubmittingCreate}
                                    avatarSize={165}
                                    sx={{ position: 'absolute', inset: 0, minHeight: 165 }}
                                />
                            </Box>
                        </Box>
                        <Box className="menu-library-create-dialog__base-left-fields">
                            <Box
                                className="menu-library-create-dialog__name-lang-row"
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                gap={2}
                                flexWrap="wrap"
                            >
                                <Typography component="span" className="menu-library-create-dialog__h-title">
                                    {i18n.t('second_menu_component_text_name')}
                                </Typography>
                                {/*
                                 * 一级菜单名称：`createDialogFirstMenuDisplayLang` + `newMenuPrimaryName` / `createDialogMainGroupNameI18nJson`（与 RBP2 `main_group_name` / `main_group_name_i18n` 一致）。
                                 */}
                                <MenuLanguageMenuButton
                                    state={state}
                                    value={state.createDialogFirstMenuDisplayLang}
                                    onChange={(lang) => state.setCreateDialogFirstMenuDisplayLang(lang)}
                                />
                            </Box>
                            <CommonInput
                                label=""
                                required
                                regularType="required"
                                inputType="INPUT"
                                variant="outlined"
                                fullWidth
                                className="menu-library-create-dialog__name-input"
                                value={state.newMenuName}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    state.setNewMenuName(e.currentTarget.value);
                                }}
                            />
                            <FormFieldCell
                                labelKey="menu_library_detail_category_limit_label"
                                cellClassName="menu-library-create-dialog__max-limit-row"
                            >
                                <CommonInput
                                    inputType="INPUT"
                                    type="number"
                                    variant="outlined"
                                    fullWidth
                                    className="menu-library-create-dialog__max-limit-input"
                                    label=""
                                    inputProps={{ min: 1, step: 1 }}
                                    value={state.createDialogMaxLimitDraft}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        state.setCreateDialogMaxLimitDraft(e.currentTarget.value);
                                    }}
                                    disabled={state.isSubmittingCreate}
                                />
                            </FormFieldCell>

                            {/**
                             * 「当前菜单菜品不受轮次限制」仅云端新建展示并写入 `menus/create`；
                             * 本地新增菜单隐藏该开关。
                             */}
                            {state.isCloudCreateDialog ? (
                                <Box
                                    className="menu-library-create-dialog__round-row"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap={2}
                                    flexWrap="wrap"
                                >
                                    <Typography component="span" className="menu-library-create-dialog__round-label">
                                        {i18n.t('menu_library_create_dialog_round_limit_label')}
                                    </Typography>
                                    <AntSwitch
                                        checked={state.createMenuRoundLimitUnrestricted}
                                        onChange={(_e, checked) => state.setCreateMenuRoundLimitUnrestricted(checked)}
                                        inputProps={{
                                            'aria-label': i18n.t('menu_library_create_dialog_round_limit_label'),
                                        }}
                                    />
                                </Box>
                            ) : null}
                        </Box>
                    </Box>

                    <Box className="menu-library-create-dialog__base-divider" aria-hidden />

                    <Box className="menu-library-create-dialog__base-right">
                        <Box
                            className="menu-library-create-dialog__price-header"
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={1}
                            mb={2}
                        >
                            {/**
                             * 云端新建不展示绑定费用：「菜单价格」标题一并隐藏，仅保留保存入口。
                             */}
                            {!state.isCloudCreateDialog ? (
                                <Typography component="span" className="menu-library-create-dialog__h-title">
                                    {i18n.t('menu_library_create_dialog_menu_price_title')}
                                </Typography>
                            ) : (
                                <Box component="span" aria-hidden />
                            )}
                            <Button
                                type="button"
                                variant="contained"
                                size="small"
                                className="menu-library-create-dialog__save-inline"
                                disabled={state.isSubmittingCreate}
                                startIcon={<SaveIcon className="menu-library-create-dialog__save-inline-icon" aria-hidden />}
                                onClick={() => void state.submitCreateFirstMenu()}
                            >
                                {i18n.t('menu_library_detail_save_changes')}
                            </Button>
                        </Box>
                        {!state.isCloudCreateDialog && state.createDialogSetItemMain === '1' ? (
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            spacing={1}
                            alignItems="center"
                            className="menu-library-create-dialog__week-row menu-library-create-dialog__week-row--compact"
                            mb={2}
                            sx={{ gap: 1 }}
                        >
                            {CREATE_MENU_WEEK_KEYS.map((key) => {
                                const selected = selectedWeek === key;
                                return (
                                    <Button
                                        key={String(key)}
                                        type="button"
                                        variant={selected ? 'contained' : 'outlined'}
                                        color="primary"
                                        className="menu-library-create-dialog__week-pill"
                                        onClick={() => state.setCreateMenuSelectedWeekKey(key)}
                                    >
                                        {weekPillLabel(key)}
                                    </Button>
                                );
                            })}
                            <IconButton
                                type="button"
                                color="primary"
                                className="menu-library-create-dialog__week-copy-btn"
                                aria-label={i18n.t('business_hour_page_click_copy')}
                                onClick={() => setCreateCopyConfirmOpen(true)}
                            >
                                <CopyWeekPriceIcon className="menu-library-create-dialog__week-copy-icon" aria-hidden />
                            </IconButton>
                            <AlertDialog
                                open={createCopyConfirmOpen}
                                size="xs"
                                title={i18n.t('business_hour_page_click_copy')}
                                desc={i18n.t('business_hour_page_this_operate_will_cover_others_if_continue')}
                                onCancel={() => setCreateCopyConfirmOpen(false)}
                                onConfirm={() => {
                                    state.createMenuOneClickCopy();
                                    setCreateCopyConfirmOpen(false);
                                }}
                            />
                        </Stack>
                        ) : null}
                        {!state.isCloudCreateDialog ? (
                        <>
                            <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                gap={1}
                                className="menu-library-menu-detail__price-pills menu-library-menu-detail__price-pills--edit"
                            >
                                {(
                                    [
                                        {
                                            Icon: MenuLibraryPriceAdultFaceIcon,
                                            labelKey: 'menu_library_detail_price_adult',
                                            fieldKey: 'adult' as const,
                                            extraField: PRICE_KEY_TO_EXTRA.adult,
                                        },
                                        {
                                            Icon: ChildCareIcon,
                                            labelKey: 'menu_library_detail_price_child',
                                            fieldKey: 'child' as const,
                                            extraField: PRICE_KEY_TO_EXTRA.child,
                                        },
                                        {
                                            Icon: FaceSmileIcon,
                                            labelKey: 'menu_library_detail_price_other',
                                            fieldKey: 'other' as const,
                                            extraField: PRICE_KEY_TO_EXTRA.other,
                                        },
                                    ] as const
                                ).map(({ Icon, labelKey, fieldKey, extraField }) => (
                                    <Paper
                                        key={fieldKey}
                                        variant="outlined"
                                        component="button"
                                        type="button"
                                        className="menu-library-menu-detail__price-pill menu-library-menu-detail__price-pill--clickable menu-library-menu-detail__price-pill--edit-trigger"
                                        onClick={(e) => {
                                            setPriceEditMenuField(fieldKey);
                                            setPriceEditMenuAnchor(e.currentTarget);
                                        }}
                                        aria-haspopup="listbox"
                                        aria-expanded={
                                            Boolean(priceEditMenuAnchor) && priceEditMenuField === fieldKey
                                        }
                                    >
                                        <Icon className="menu-library-menu-detail__price-pill-icon" aria-hidden />
                                        <Typography
                                            variant="body1"
                                            className="menu-library-menu-detail__price-pill-text menu-library-menu-detail__price-pill-text--edit-line"
                                            noWrap
                                        >
                                            {formatPriceEditPillLine(
                                                labelKey,
                                                state.getCreateEditPillSumDisplay(extraField),
                                            )}
                                        </Typography>
                                        <ArrowDropdownIcon
                                            className="menu-library-menu-detail__price-pill-dropdown-arrow"
                                            aria-hidden
                                        />
                                    </Paper>
                                ))}
                            </Stack>
                            <Menu
                                anchorEl={priceEditMenuAnchor}
                                open={Boolean(priceEditMenuAnchor && priceEditMenuField)}
                                onClose={() => {
                                    setPriceEditMenuAnchor(null);
                                    setPriceEditMenuField(null);
                                }}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                PaperProps={{
                                    className: 'menu-library-menu-detail__price-edit-menu-paper',
                                }}
                                MenuListProps={{ autoFocusItem: false, dense: true }}
                            >
                                {priceEditMenuItems.map((item) => {
                                    const extra = priceEditMenuField
                                        ? PRICE_KEY_TO_EXTRA[priceEditMenuField]
                                        : null;
                                    const ids = extra ? state.getCreateExtraIdsForField(extra) : [];
                                    const selected =
                                        !!extra &&
                                        (item.value === ''
                                            ? ids.length === 0
                                            : ids.includes(item.value));
                                    return (
                                        <ListItemButton
                                            key={item.value === '' ? '__empty' : item.value}
                                            selected={selected}
                                            role="menuitem"
                                            onClick={() => {
                                                if (!extra) {
                                                    return;
                                                }
                                                if (item.value === '') {
                                                    state.clearCreateExtraField(extra);
                                                } else {
                                                    state.toggleCreateExtraIdInMenu(extra, item.value);
                                                }
                                            }}
                                        >
                                            <ListItemText primary={item.label} />
                                        </ListItemButton>
                                    );
                                })}
                            </Menu>
                        </>
                        ) : null}
                    </Box>
                </Box>
            </Paper>
            {/*
             * 结构：sub-wrap 占满「顶卡与弹窗底栏」之间的剩余高度（flex:1）；
             * sub-scroll 吸收多余空间并 overflow-y:auto；新增按钮在 sub-wrap 末尾 flex-shrink:0，始终贴该区块底边。
             */}
            <Box className="menu-library-create-dialog__sub-wrap">
                <Box ref={subcategoryListScrollRef} className="menu-library-create-dialog__sub-scroll">
                    {drafts.map((row) => (
                        <CreateMenuSubcategoryDraftRow
                            key={row.tempId}
                            state={state}
                            row={row}
                            subcategoryEdit={subcategoryEdit}
                            setSubcategoryEdit={setSubcategoryEdit}
                            setSubcategoryDeleteConfirmTempId={setSubcategoryDeleteConfirmTempId}
                        />
                    ))}
                </Box>
                <Button
                    type="button"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    className="menu-library-create-dialog__add-sub menu-library-create-dialog__add-sub--bar menu-library-create-dialog__sub-wrap-footer"
                    sx={{ flexShrink: 0, mt: 1 }}
                    disabled={state.isSubmittingCreate}
                    onClick={() => void state.addCreateMenuSubcategoryRowOrPersist()}
                >
                    <span className="menu-library-create-dialog__add-sub-inner">
                        <Typography component="span" className="menu-library-create-dialog__add-sub-label">
                            {i18n.t('menu_library_create_dialog_add_subcategory')}
                        </Typography>
                        <AddIcon className="menu-library-create-dialog__add-sub-icon" aria-hidden />
                    </span>
                </Button>
            </Box>
            <AlertDialog
                open={subcategoryDeleteConfirmTempId != null}
                variant="delete"
                size="sm"
                title={i18n.t('menu_library_create_dialog_delete_subcategory_title')}
                bodyPrompt={i18n.t('alert_dialog_delete_confirm_question')}
                highlightText={(() => {
                    const tid = subcategoryDeleteConfirmTempId;
                    if (!tid) {
                        return '';
                    }
                    const row = drafts.find((r) => r.tempId === tid);
                    if (!row) {
                        return '—';
                    }
                    const name = getMainGroupNameForDisplay({
                        primary: row.name,
                        i18nJson: row.nameI18nJson,
                        lang: row.displayLang,
                    }).trim();
                    return name.length > 0 ? name : '—';
                })()}
                confirmText={i18n.t('menu_library_create_dialog_confirm_delete_subcategory_action')}
                onCancel={() => setSubcategoryDeleteConfirmTempId(null)}
                onConfirm={() => {
                    const tempId = subcategoryDeleteConfirmTempId;
                    setSubcategoryDeleteConfirmTempId(null);
                    if (tempId) {
                        void state.deleteCreateMenuSubcategoryDraftConfirmed(tempId);
                    }
                }}
            />
        </Box>
    );
});

/**
 * 「新建菜单」整窗：DialogComponent + 侧栏避让 + 纸面外边距
 */
const MenuLibraryCreateMenuDialog: React.FC<ShellProps> = observer(({ state }) => {
    const mainPageData = MobxObjectsManager.get(MainPageState);
    /** 与 main-page 一致：侧栏展开 / 收起 / 移动端隐藏 */
    const sidebarOffset = useMemo(() => {
        const { open, isMobileDevice, sidebarWidth } = mainPageData;
        return open ? sidebarWidth.expand : isMobileDevice ? sidebarWidth.hidden : sidebarWidth.reduced;
    }, [mainPageData.open, mainPageData.isMobileDevice, mainPageData.sidebarWidth]);

    /** 纸面四周统一外边距（与 CREATE_MENU_DIALOG_OUTER_MARGIN_PX 一致） */
    const outerMarginPx = CREATE_MENU_DIALOG_OUTER_MARGIN_PX;


    return (
        <>
            <DialogComponent
                open={state.isCreateMenuDialogOpen}
                fullWidth
                maxWidth={false}
                PaperProps={{
                    sx: {
                        position: 'fixed',
                        left: `calc(${sidebarOffset} + ${outerMarginPx}px)`,
                        top: outerMarginPx,
                        width: `calc(100% - ${sidebarOffset} - ${2 * outerMarginPx}px)`,
                        maxWidth: 'none !important',
                        height: `calc(100vh - ${2 * outerMarginPx}px)`,
                        maxHeight: `calc(100vh - ${2 * outerMarginPx}px)`,
                        margin: 0,
                        borderRadius: '16px',
                        border: '2px solid var(--fill-primary-300)',
                        background: 'var(--fill-primary-200)',
                        boxShadow: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    },
                }}
                sx={{
                    '& .MuiDialog-container': {
                        alignItems: 'stretch',
                        justifyContent: 'flex-start',
                        backgroundColor: 'var(--overlay-800-a25)',
                    },
                    '& .MuiBackdrop-root': {
                        left: sidebarOffset,
                        width: `calc(100% - ${sidebarOffset})`,
                    },
                    /** 覆盖 DialogComponent 默认 Paper 样式，与本弹窗独立圆角/描边一致 */
                    '& .MuiDialog-container > .MuiPaper-root': {
                        borderRadius: '16px',
                        border: '2px solid var(--fill-primary-300)',
                        background: 'var(--fill-primary-200)',
                        boxShadow: 'none',
                    },
                    '& .MuiDialog-paper': {
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: `calc(100vh - ${2 * outerMarginPx}px)`,
                    },
                    /** 仅子类别列表内部滚动：内容区占满标题与底部按钮之间的剩余高度 */
                    '& .MuiDialogContent-root': {
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
                defaultHeaderProps={{
                    title: i18n.t('menu_library_create_dialog_title'),
                    onClose: () => state.closeCreateMenuDialog(),
                }}
            >
                <MenuLibraryCreateMenuDialogForm state={state} />
            </DialogComponent>

            <DialogComponent
                open={state.isCreateMenuSubcategoryAddDishDialogOpen}
                fullWidth
                maxWidth={false}
                dialogContentStyle={{ paddingTop: 0 }}
                onClose={() => state.closeCreateMenuSubcategoryAddDishDialog()}
                PaperProps={{
                    sx: {
                        width: '80%',
                        maxWidth: 'min(1120px, 96vw)',
                        margin: '16px auto',
                        maxHeight: 'calc(100vh - 32px)',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
                sx={{
                    '& .MuiDialogContent-root': {
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        paddingBottom: 0,
                    },
                }}
                defaultHeaderProps={{
                    title:
                        state.menuLibraryAddDishDialogMode === 'batch'
                            ? i18n.t('menu_library_create_dialog_batch_dish_settings')
                            : state.createMenuSubcategoryAddDishEditRowKey != null ||
                                state.menuLibraryDetailAddDishEditItemId != null
                                ? i18n.t('menu_library_detail_dish_dialog_title')
                                : i18n.t('menu_library_create_dialog_add_dish_dialog_title'),
                    onClose: () => state.closeCreateMenuSubcategoryAddDishDialog(),
                }}
            >
                {/* 菜单库详情下 key 须稳定：promote 后 itemId 变化不应 remount，否则 courseGroupDialogOpen 等 state 会丢 */}
                <MenuLibraryAddDishDialogContent
                    key={
                        state.menuLibraryAddDishDialogMode === 'batch'
                            ? `batch|${state.batchDishSettingsRowKeys.join(',')}`
                            : state.createMenuSubcategoryAddDishEditRowKey ?? 'menu-library-add-dish-dialog'
                    }
                    state={state}
                    comboIconColor="var(--fill-primary-300)"
                    onClose={() => state.closeCreateMenuSubcategoryAddDishDialog()}
                />
            </DialogComponent>
        </>
    );
});

export default MenuLibraryCreateMenuDialog;
